import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/auth.store';
import type { Supplier } from '@haccp/api-client';

const UserGroupIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const PhoneIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const MailIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PencilIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

export default function SuppliersSubPage({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => apiClient.getSuppliers(),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; phone?: string; email?: string }) =>
      apiClient.createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      resetForm();
      addToast(t('suppliers.added'), 'success');
    },
    onError: () => addToast(t('suppliers.addError'), 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; phone?: string; email?: string } }) =>
      apiClient.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      resetForm();
      addToast(t('suppliers.updated'), 'success');
    },
    onError: () => addToast(t('suppliers.updateError'), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      addToast(t('suppliers.deleted'), 'success');
    },
    onError: () => addToast(t('suppliers.deleteError'), 'error'),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingSupplier(null);
    setFormData({ name: '', phone: '', email: '' });
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({ name: supplier.name, phone: supplier.phone || '', email: supplier.email || '' });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSupplier) {
      updateMutation.mutate({ id: editingSupplier.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const canWrite = user?.role === 'OWNER' || user?.role === 'STAFF';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 my-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <UserGroupIcon className="w-6 h-6 text-primary-600" /> {t('suppliers.title')}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        <div className="mb-4">
          {canWrite && (
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl hover:shadow-lg transition font-semibold mb-2"
            >
              {t('suppliers.newBtn')}
            </button>
          )}
        </div>

        {showForm && (
          <div className="mb-4">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('suppliers.nameLabel')}</label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={formData.name}
                  onChange={(e) => setFormData(d => ({ ...d, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('suppliers.namePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('suppliers.phoneLabel')}</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData(d => ({ ...d, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('suppliers.phonePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('suppliers.emailLabel')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(d => ({ ...d, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('suppliers.emailPlaceholder')}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition font-semibold"
                >
                  {t('suppliers.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl hover:shadow-lg transition font-semibold disabled:opacity-50"
                >
                  {editingSupplier ? t('suppliers.update') : t('suppliers.save')}
                </button>
              </div>
            </form>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-8 text-slate-500">{t('suppliers.empty')}</div>
        ) : (
          <div className="space-y-2">
            {suppliers.map((supplier: Supplier) => (
              <div key={supplier.id} className="bg-slate-50 rounded-xl p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <UserGroupIcon className="w-5 h-5 text-primary-500" />
                  <div>
                    <div className="font-semibold text-slate-900">{supplier.name}</div>
                    <div className="text-xs text-slate-500 flex gap-2">
                      {supplier.phone && <span className="flex items-center gap-1"><PhoneIcon className="w-4 h-4" />{supplier.phone}</span>}
                      {supplier.email && <span className="flex items-center gap-1"><MailIcon className="w-4 h-4" />{supplier.email}</span>}
                    </div>
                  </div>
                </div>
                {canWrite && (
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(supplier)} className="p-1 rounded hover:bg-primary-100"><PencilIcon className="w-4 h-4 text-primary-600" /></button>
                    <button onClick={() => { if (confirm(t('suppliers.deleteConfirmTitle'))) deleteMutation.mutate(supplier.id); }} className="p-1 rounded hover:bg-red-100"><TrashIcon className="w-4 h-4 text-red-600" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
